import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { jobTypeOptions } from "@src/appV2/redesign/PlacementCandidate/constants";
import { JobType } from "@src/appV2/redesign/PlacementCandidate/types";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import {
  computeScheduleType,
  computeWorkdaysRequired,
  formatFixedDays,
  formatFlexibleSchedule,
  getPlacementLatestDateInfo,
} from "../utils";
import { ListHelper } from "./ListHelper";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";

interface PlacementScheduleSectionProps {
  placement: Pick<
    PlacementWithDetails,
    "id" | "jobShifts" | "createdAt" | "updatedAt" | "confirmedAt"
  >;
}

export function PlacementScheduleSection(props: PlacementScheduleSectionProps) {
  const { placement } = props;
  const { id: placementId, jobShifts, createdAt, updatedAt, confirmedAt } = placement;

  const jobTypePriority: Record<JobType, number> = {
    [JobType.FULL_TIME]: 1,
    [JobType.PART_TIME]: 2,
    [JobType.PER_DIEM]: 3,
    [JobType.WEEKEND_WARRIOR]: 4,
  };

  const mappedShiftOptions = jobShifts
    .map((jobShift) => {
      const { jobType, daysOfWeek, notes, minimumRequiredDaysPerWeek } = jobShift;
      const scheduleType = computeScheduleType(jobShift);

      return {
        jobType,
        label: jobTypeOptions.find((option) => option.value === jobType)?.label,
        scheduleType,
        workdaysRequired:
          scheduleType === "flexible" ? computeWorkdaysRequired(daysOfWeek) : undefined,
        notes,
        daysOfWeek,
        minimumRequiredDaysPerWeek,
      };
    })
    .filter((item) => isDefined(item.label));

  // Sort by job type priority
  const sortedShiftOptions = [...mappedShiftOptions].sort((a, b) => {
    const priorityA = jobTypePriority[a.jobType] ?? 4;
    const priorityB = jobTypePriority[b.jobType] ?? 4;
    return priorityA - priorityB;
  });

  // Count occurrences of each job type
  const jobTypeCounts = sortedShiftOptions.reduce<Record<JobType, number>>(
    (accumulator, item) => {
      accumulator[item.jobType] = (accumulator[item.jobType] ?? 0) + 1;
      return accumulator;
    },
    {
      [JobType.FULL_TIME]: 0,
      [JobType.PART_TIME]: 0,
      [JobType.PER_DIEM]: 0,
      [JobType.WEEKEND_WARRIOR]: 0,
    }
  );

  // Track current index for each job type
  const jobTypeIndices: Record<JobType, number> = {
    [JobType.FULL_TIME]: 0,
    [JobType.PART_TIME]: 0,
    [JobType.PER_DIEM]: 0,
    [JobType.WEEKEND_WARRIOR]: 0,
  };

  // Add indices to labels when there are multiple of the same job type
  const mapShiftOptions = sortedShiftOptions.map((item) => {
    const count = jobTypeCounts[item.jobType] ?? 1;
    jobTypeIndices[item.jobType] += 1;
    const currentIndex = jobTypeIndices[item.jobType];
    const displayLabel = count > 1 ? `${item.label} ${currentIndex}` : item.label;

    return {
      ...item,
      label: displayLabel,
    };
  });

  const placementLatestDateInfo = getPlacementLatestDateInfo(createdAt, updatedAt, confirmedAt);

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS,
    {
      action: "schedule_viewed",
      placementId,
      mapShiftOptions,
      placementLatestDateInfo,
    },
    {
      enabled:
        isDefined(placementLatestDateInfo) && isDefined(placementId) && mapShiftOptions.length > 0,
    }
  );

  return (
    <PlacementSectionTemplate title="Schedule" iconType="calendar-clock">
      <Stack spacing={3}>
        {placementLatestDateInfo && (
          <Text variant="caption" sx={{ color: (theme) => theme.palette.text.tertiary }}>
            Listing {placementLatestDateInfo.action}: {placementLatestDateInfo.formattedDistance}{" "}
            ago
          </Text>
        )}
        <ListHelper>
          {mapShiftOptions.map(
            ({
              label,
              notes,
              scheduleType,
              daysOfWeek,
              minimumRequiredDaysPerWeek,
              workdaysRequired,
            }) => (
              <ListHelper.Item key={label}>
                <Text variant="body2">
                  {label}: {scheduleType === "4_on_2_off" && "4 days on, 2 days off"}
                  {scheduleType === "flexible" &&
                    isDefined(minimumRequiredDaysPerWeek) &&
                    workdaysRequired &&
                    formatFlexibleSchedule(minimumRequiredDaysPerWeek, workdaysRequired)}
                  {(scheduleType === "fixed" || !scheduleType) &&
                    daysOfWeek?.length > 0 &&
                    formatFixedDays(daysOfWeek)}
                  {scheduleType === "other" && notes && notes}
                </Text>
              </ListHelper.Item>
            )
          )}
        </ListHelper>
      </Stack>
    </PlacementSectionTemplate>
  );
}
